








































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import Modal from '@/components/UI/Modal.vue';
import { copyTextToClipboard } from '../core/download';

const COPIED_FEEDBACK_MS = 2000;

/** Код клавиши «A» — запасной путь для браузеров без `KeyboardEvent.key`. */
const KEY_CODE_A = 65;

const ANT_SELECT_SEARCH_CLASS = 'ant-select-search__field';

const EDITABLE_LOOKUP_DEPTH = 5;

@Component({
	components: { Modal },
})
export default class MmScrollablePopup extends Vue {
	@Prop({ required: true }) readonly visible!: boolean;

	@Prop({ default: '' }) readonly title!: string;

	@Prop({ default: '' }) readonly subtitle!: string;

	@Prop({ default: () => [] }) readonly lines!: string[];

	@Prop({ default: false }) readonly hideCopy!: boolean;

	isCopied: boolean = false;

	private copiedTimer: number | null = null;

	private isKeyHandlerAttached: boolean = false;

	get hasLines(): boolean {
		return !!this.lines && this.lines.length > 0;
	}

	get showCopyButton(): boolean {
		return this.hasLines && this.hideCopy !== true;
	}

	get wrapClassName(): string {
		return this.hideCopy === true
			? 'mm-scrollable-popup-modal mm-scrollable-popup-modal--compact'
			: 'mm-scrollable-popup-modal';
	}

	get footerButtonSize(): string {
		return this.hideCopy === true ? 'small' : 'default';
	}

	/** Метод, а не геттер: слоты не реактивны, геттер запомнил бы отрисовку ещё закрытого окна */
	hasActionsSlot(): boolean {
		const scoped = this.$scopedSlots as { [key: string]: any };

		return !!this.$slots.actions || !!scoped.actions;
	}

	get text(): string {
		return this.hasLines ? this.lines.join('\n') : '';
	}

	get copyButtonText(): string {
		if (this.isCopied) {
			return this.$t('priceMonitoring.mobileMonitoring.states.copied') as string;
		}

		return this.$t('priceMonitoring.mobileMonitoring.common.copyAll') as string;
	}

	get closeText(): string {
		return this.$t('priceMonitoring.mobileMonitoring.common.close') as string;
	}

	mounted(): void {
		if (this.visible) {
			this.attachKeyHandler();
		}
	}

	@Watch('visible')
	onVisibleChanged(next: boolean): void {
		if (next) {
			this.attachKeyHandler();

			return;
		}

		this.detachKeyHandler();
		this.resetCopiedFeedback();
	}

	@Watch('lines')
	onLinesChanged(): void {
		this.resetCopiedFeedback();
	}

	handleCopyAll(): void {
		if (!this.hasLines) {
			return;
		}

		const ok = copyTextToClipboard(this.text);

		if (!ok) {
			this.selectAllText();

			return;
		}

		this.isCopied = true;
		this.clearCopiedTimer();
		this.copiedTimer = window.setTimeout(() => {
			this.isCopied = false;
			this.copiedTimer = null;
		}, COPIED_FEEDBACK_MS);
	}

	handleClose(): void {
		this.$emit('close');
	}

	beforeDestroy(): void {
		this.detachKeyHandler();
		this.clearCopiedTimer();
	}

	/** Слушатель в фазе перехвата: иначе Ctrl/Cmd+A успевает выделить всю страницу */
	private onDocumentKeydown(event: KeyboardEvent): void {
		if (!this.visible || !this.hasLines) {
			return;
		}

		const isModifier = event.ctrlKey === true || event.metaKey === true;
		const isLetterA = event.key === 'a' || event.key === 'A' || event.keyCode === KEY_CODE_A;

		if (!isModifier || event.altKey === true || !isLetterA) {
			return;
		}

		/** События из полей ввода не перехватываем: слушатель на document срабатывает раньше поля */
		if (this.isEventFromEditable(event)) {
			return;
		}

		if (this.selectAllText()) {
			event.preventDefault();
		}
	}

	/** Вверх по родителям вручную: closest есть не во всех браузерах, а className у SVG не строка */
	private isEventFromEditable(event: KeyboardEvent): boolean {
		let node: HTMLElement | null = event && event.target ? (event.target as HTMLElement) : null;
		let depth = 0;

		while (node && depth < EDITABLE_LOOKUP_DEPTH) {
			const tag = node.tagName ? String(node.tagName).toUpperCase() : '';

			if (tag === 'INPUT' || tag === 'TEXTAREA') {
				return true;
			}

			if (node.isContentEditable === true) {
				return true;
			}

			const className = typeof node.className === 'string' ? node.className : '';

			if (className.indexOf(ANT_SELECT_SEARCH_CLASS) !== -1) {
				return true;
			}

			node = node.parentElement;
			depth += 1;
		}

		return false;
	}

	private selectAllText(): boolean {
		const node = this.$refs.text as HTMLElement;

		if (!node || !window.getSelection || !document.createRange) {
			return false;
		}

		const selection = window.getSelection();

		if (!selection) {
			return false;
		}

		const range = document.createRange();

		range.selectNodeContents(node);
		selection.removeAllRanges();
		selection.addRange(range);

		return true;
	}

	private attachKeyHandler(): void {
		if (this.isKeyHandlerAttached) {
			return;
		}

		document.addEventListener('keydown', this.onDocumentKeydown, true);
		this.isKeyHandlerAttached = true;
	}

	private detachKeyHandler(): void {
		if (!this.isKeyHandlerAttached) {
			return;
		}

		document.removeEventListener('keydown', this.onDocumentKeydown, true);
		this.isKeyHandlerAttached = false;
	}

	private resetCopiedFeedback(): void {
		this.isCopied = false;
		this.clearCopiedTimer();
	}

	private clearCopiedTimer(): void {
		if (this.copiedTimer !== null) {
			window.clearTimeout(this.copiedTimer);
			this.copiedTimer = null;
		}
	}
}
